import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppContext, Product } from "../../context/AppContext";
import { useCopy } from "../../context/CopyContext";
import Header from "../Header";
import Heading from "../Heading";
import Button from "../Button";
import PoweredBy from "../PoweredBy";
import ItemsModalView from "./ItemsModalView";
import CategorySelector from "../CategorySelector";

const EmptyAcceptedView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, dispatch } = useAppContext();
  const { getCopy } = useCopy();
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [hasAddedItem, setHasAddedItem] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [scrollDirection, setScrollDirection] = useState<'left' | 'right'>('right');
  
  // Check if this is a back navigation from email capture
  const isBackFromEmail = location.state?.fromEmailCapture === true;

  // Get the most recently added product from state
  const lastAddedProduct = state.lastAddedProduct;
  
  // Check if the current product is non-branded
  // If force unbranded is enabled in test mode, always treat as non-branded
  const isNonBrandedProduct = lastAddedProduct && (!lastAddedProduct.isBranded || state.settings.forceUnbranded);
  
  // Check if continue should be enabled (branded product OR non-branded product with category selected)
  const canContinue = !isNonBrandedProduct || (isNonBrandedProduct && selectedCategory);

  // Load products from JSON file
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const response = await fetch('/mock-products.json');
        const productsData = await response.json();
        setProducts(productsData);
      } catch (error) {
        console.error('Failed to load products:', error);
      }
    };

    loadProducts();
  }, []);

  // Add an empty when component mounts (only if not coming back from email capture)
  useEffect(() => {
    if (products.length > 0 && !hasAddedItem && !isBackFromEmail) {
      const randomIndex = Math.floor(Math.random() * products.length);
      const selectedProduct = products[randomIndex];
      
      // Preview the selected product without adding to empties yet
      dispatch({ type: 'PREVIEW_PRODUCT', payload: selectedProduct });
      setHasAddedItem(true);
    }
  }, [products, hasAddedItem, dispatch, isBackFromEmail]);

  const handleRemoveItem = () => {
    navigate("/remove-empty");
  };

  const handleContinue = () => {
    // For non-branded products, create the categorized product and store it as lastAddedProduct
    if (isNonBrandedProduct && lastAddedProduct && selectedCategory) {
      // Create a new product object as an unbranded empty item
      const categorizedProduct: Product = {
        id: `unbranded-empty-${selectedCategory.id}-${Date.now()}`, // Unique ID for unbranded item
        name: "Empty", // Generic name for unbranded items
        brand: "Unbranded", // Generic brand
        image: selectedCategory.image, // Use the category image
        backDropImage: selectedCategory.image, // Use the category image as backdrop too
        category: selectedCategory.name, // The selected category
        size: "Various", // Generic size
        price: 0, // No price for unbranded items
        rewardValue: 0, // No reward value
        isBranded: false, // Mark as unbranded
        rewards: {
          loyaltyPoints: 0,
          dollarValue: "$0.00",
          discountValue: "0%"
        }
      };
      
      console.log('Creating categorized product for later dispense:', {
        originalProduct: lastAddedProduct,
        categorizedProduct,
        selectedCategory
      });
      
      // Update the lastAddedProduct with the categorized version
      // This will be added to the tray when dispensed in PlaceEmptyView
      dispatch({ 
        type: 'PREVIEW_PRODUCT', 
        payload: categorizedProduct
      });
    }
    
    dispatch({ type: "SET_STEP", payload: 5.5 });
    navigate("/place-empty");
  };

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  const handleCategorySelect = (category: any) => {
    setSelectedCategory(category);
  };

  const handleScrollDirectionChange = (direction: 'left' | 'right') => {
    setScrollDirection(direction);
  };

  return (
    <div
      className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden"
      data-node-id="74:974"
    >
      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-24">
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={state.itemsCount > 0}
              itemsNumber={state.itemsCount.toString()}
              onItemsClick={handleItemsClick}
              
            />
          </div>
        </div>

        {/* Main Content Area - with 3-row grid */}
        <motion.div
          className="flex-1 grid grid-rows-3 gap-0 mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {isNonBrandedProduct ? (
            /* Non-branded product layout with category selection */
            <>
              {/* Row 1 - Main heading */}
              <div className="flex items-center justify-center">
                <div className="text-center w-full max-w-[920px]">
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 0.6,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                  >
                    <Heading title={getCopy('emptyAccepted.identifiedHeading')} />
                  </motion.div>
                </div>
              </div>

              {/* Rows 2 & 3 - Product image and category selection spanning two rows */}
              <div className="row-span-2 flex flex-col items-center justify-center">
                {/* Product image - taking available space */}
                <motion.div
                  className="w-full max-w-[468px] h-[440px] flex items-center justify-center mb-8"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{
                    duration: 0.8,
                    delay: 0.2,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                >
                  <AnimatePresence mode="wait">
                    <motion.div
                      className="w-full h-full bg-center bg-cover bg-no-repeat"
                      key={selectedCategory?.id || 'default'}
                      initial={{ 
                        opacity: 0, 
                        x: scrollDirection === 'right' ? 50 : -50 
                      }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ 
                        opacity: 0, 
                        x: scrollDirection === 'right' ? -50 : 50 
                      }}
                      transition={{
                        duration: 0.3,
                        ease: [0.25, 0.1, 0.25, 1],
                      }}
                      style={{
                        backgroundImage: selectedCategory 
                          ? `url('${selectedCategory.image}')` 
                          : "url('/category-images/ceramic-porcelain.png')",
                        backgroundSize: "contain",
                        backgroundRepeat: "no-repeat",
                      }}
                    />
                  </AnimatePresence>
                </motion.div>
              </div>
            </>
            
          ) : (
            /* Branded product layout */
            <>
              {/* Row 1 - Main heading */}
              <div className="flex items-center justify-center">
                <div className="text-center w-full max-w-[920px]">
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 0.6,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                  >
                    <Heading title={getCopy('emptyAccepted.heading')} />
                  </motion.div>
                </div>
              </div>

              {/* Rows 2 & 3 - Product image and details spanning two rows */}
              <div className="row-span-2 flex flex-col items-center justify-center">
                {/* Product image - taking all available space minus text height */}
                <motion.div
                  className="w-full flex-1 bg-center bg-cover bg-no-repeat"
                  style={{
                    backgroundImage: lastAddedProduct 
                      ? `url('${lastAddedProduct.image}')` 
                      : "url('/brand-ultra-facial-cream.png')",
                    backgroundSize: "contain",
                    backgroundRepeat: "no-repeat",
                  }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{
                    duration: 0.8,
                    delay: 0.2,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                />
                
                {/* Product details - natural flow at bottom */}
                <motion.div
                  className="text-center w-full max-w-[920px] flex-shrink-0 mt-24"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: 0.4,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                >
                  <div className="flex flex-col gap-4 items-center py-4">
                    <h3 className="font-brand-secondary font-normal text-[32px] leading-[42px] text-brand-text-primary text-center">
                      {lastAddedProduct ? lastAddedProduct.name : getCopy('productFallback.defaultProductName')}
                    </h3>
                    <div className="flex items-center justify-center">
                      <span className="font-brand-secondary font-normal text-[24px] leading-[31px] text-brand-text-muted text-center">
                        {lastAddedProduct ? lastAddedProduct.size : getCopy('productFallback.defaultProductSize')}
                      </span>
                    </div>
                    
                    {/* Reward Values - Only show for branded products */}
                    {((lastAddedProduct && lastAddedProduct.isBranded && lastAddedProduct.rewards) || (!lastAddedProduct)) && (
                      <motion.div
                        className="flex flex-col gap-2 items-center mt-6"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.5,
                          delay: 0.6,
                          ease: [0.25, 0.1, 0.25, 1],
                        }}
                      >
                        <div className="font-brand-secondary font-normal text-2xl mb-2 leading-[23px] text-brand-text-primary text-center">
                          Reward value
                        </div>
                        <div className="flex flex-row items-start justify-start">
                          {/* Left section - Points */}
                          <div className="bg-brand-tertiary flex flex-row gap-2.5 items-center justify-center pl-4 pr-3 py-1 rounded-bl-[100px] rounded-tl-[100px]">
                            <div className="font-brand-secondary font-medium text-[24px] leading-[31px] text-white text-center w-[53px]">
                              {lastAddedProduct && lastAddedProduct.rewards ? lastAddedProduct.rewards.loyaltyPoints : 0}
                            </div>
                          </div>
                          {/* Middle section - Dollar Value */}
                          <div className="bg-brand-tertiary flex flex-row gap-2.5 items-center justify-center px-3 py-1 relative border-l-2 border-r-2 border-brand-background">
                            <div className="font-brand-secondary font-medium text-[24px] leading-[31px] text-white text-center whitespace-nowrap">
                              {lastAddedProduct && lastAddedProduct.rewards ? lastAddedProduct.rewards.dollarValue : "$0.00"}
                            </div>
                          </div>
                          {/* Right section - Discount */}
                          <div className="bg-brand-tertiary flex flex-row gap-2.5 items-center justify-center pl-3 pr-4 py-1 rounded-br-[100px] rounded-tr-[100px] w-20">
                            <div className="font-brand-secondary font-medium text-[24px] leading-[31px] text-white text-center flex-1">
                              {lastAddedProduct && lastAddedProduct.rewards ? lastAddedProduct.rewards.discountValue : "0%"}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              </div>
            </>
          )}
        </motion.div>

        {/* Category Selection - Full Width (only for non-branded products) */}
        {isNonBrandedProduct && (
          <motion.div
            className="w-full"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.6,
              delay: 0.4,
              ease: [0.25, 0.1, 0.25, 1],
            }}
          >
            <CategorySelector
              onCategorySelect={handleCategorySelect}
              selectedCategoryId={selectedCategory?.id}
              onScrollDirectionChange={handleScrollDirectionChange}
            />
          </motion.div>
        )}

        {/* Bottom Section - with padding */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Buttons */}
          <div className="flex flex-row gap-6 lg:gap-10 w-full">
            <Button variant="secondary" onClick={handleRemoveItem}>
              {getCopy('emptyAccepted.buttons.secondary')}
            </Button>
            <Button 
              variant="primary" 
              onClick={handleContinue}
              disabled={!canContinue}
              className={!canContinue ? 'opacity-50 cursor-not-allowed' : ''}
            >
              {getCopy('emptyAccepted.buttons.primary')}
            </Button>
          </div>



          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>

      {/* Items Modal */}
      <ItemsModalView
        isOpen={showItemsModal}
        onClose={handleCloseItemsModal}
      />
    </div>
  );
};

export default EmptyAcceptedView;
